import { ShieldCheck, Check } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { AddOnTypeEnum } from 'src/shared/entities/verification-addons';
import { IconBadgeCertified, IconBadgeVerified } from 'src/components/Svg/Images';

type Props = {
  type: AddOnTypeEnum;
  actionNode?: React.ReactNode;
  className?: string;
  name: string;
  description: string;
  subscriptionText: string;
  price: number;
  benefits?: {
    heading: string;
    items: string[];
  };
};

const levelIconMap = {
  [AddOnTypeEnum.VERIFIED]: IconBadgeVerified,
  [AddOnTypeEnum.CERTIFIED]: IconBadgeCertified,
};

const LevelCard = ({ type, actionNode, className, name, description, subscriptionText, price, benefits }: Props) => {
  const onLearnMore = () => window.open('https://codekeeper.co/verification', '_blank');

  const Icon = levelIconMap[type];

  return (
    <Card className={className}>
      <CardHeader className="bg-accent space-y-2.5 border-b rounded-t-lg">
        <CardTitle className="uppercase text-center font-extrabold">{name} reports</CardTitle>
        {Icon && <Icon className="size-15 mx-auto" />}
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <div className="text-muted-foreground space-y-2">
          <ShieldCheck className="size-6" />
          <p className="text-sm font-bold">{description}</p>
        </div>
        <Separator />
        <div className="text-muted-foreground">
          <div className="text-sm mb-2.5">From</div>
          <div className="flex items-end gap-1 mb-6">
            <strong className="text-4xl text-secondary-foreground font-extrabold">${price}</strong>
            <strong className="text-2xl ">/mo</strong>
          </div>
          <p className="text-xs">{subscriptionText}</p>
        </div>
        <div className="space-y-4">
          {actionNode}
          <Button size="sm" variant="outline" className="w-full" onClick={onLearnMore}>
            Learn more
          </Button>
        </div>
        {benefits && (
          <div className="space-y-3">
            <div className="text-sm text-foreground font-medium">{benefits.heading}</div>
            <ul className="space-y-3">
              {benefits.items.map((benefit, index) => (
                <li className="flex items-center gap-2" key={index}>
                  <Check className="size-6 shrink-0 text-secondary-foreground" />
                  <span className="text-sm text-muted-foreground">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LevelCard;
